import {
  buildVideoGenerationRequestBody,
  createVideoGeneration,
  type VideoGenerationPayload,
} from '../api/seedance';
import type { Dispatch, SetStateAction } from 'react';
import type { MatNode, Task, TaskContext, TaskStatus } from '../store/seedanceTypes';
import { VIDEO_GENERATION_BASE_URL } from './seedanceConstants';
import { buildVideoGenerationMetadataItems, jsonStringifyForDebug } from './seedanceGenerationUtils';
import { stripPromptMentionsForSubmit } from './seedancePromptUtils';
import { extractApiMessage } from './seedanceRequestUtils';
import {
  formatMissingAssetSubmitMessage,
  probeMissingConnectedAssetIds,
} from './seedanceWorkspaceAssetIds';
import { normalizeStatus } from './seedanceTaskUtils';
import {
  tryBeginVideoGeneration,
  VIDEO_GENERATION_RATE_LIMIT_MESSAGE,
} from './videoGenerationRateLimit';

export interface SubmitVideoGenerationInput {
  apiKey: string;
  model: string;
  prompts: string[];
  nodes: MatNode[];
  duration: number;
  ratio: string;
  resolution: string;
  onDebug?: (message: string, isError?: boolean) => void;
}

export interface SubmitVideoGenerationResult {
  taskId: string;
  localTask: Task;
}

/** 与工作台一致的提交流程：创建本地 pending 任务并 POST 视频生成。 */
export async function submitVideoGenerationTask(
  input: SubmitVideoGenerationInput,
  hooks: {
    setTasks: Dispatch<SetStateAction<Task[]>>;
    ensurePolling: (taskId: string) => void;
  },
): Promise<SubmitVideoGenerationResult> {
  const { apiKey, model, prompts, nodes, duration, ratio, resolution, onDebug } = input;
  const allPrompts = prompts.map((p) => stripPromptMentionsForSubmit(p.trim())).filter(Boolean);
  if (!allPrompts.length) {
    throw new Error('请至少输入一个提示词');
  }
  const missingAssets = await probeMissingConnectedAssetIds(nodes, { onDebug });
  if (missingAssets.length) {
    throw new Error(formatMissingAssetSubmitMessage(missingAssets));
  }
  if (!tryBeginVideoGeneration()) {
    throw new Error(VIDEO_GENERATION_RATE_LIMIT_MESSAGE);
  }

  const userPrompt = allPrompts.join('\n');
  const prompt = userPrompt;

  const contextSnapshot: TaskContext = {
    prompts: allPrompts,
    nodes: JSON.parse(JSON.stringify(nodes)) as MatNode[],
    ratio,
    resolution,
    duration,
  };

  const localId = 'pending_' + Date.now();
  const localTask: Task = {
    id: localId,
    prompt,
    status: 'pending',
    video_url: null,
    thumbnail_url: null,
    created_at: new Date().toISOString(),
    model,
    duration,
    ratio,
    resolution,
    error: null,
    context: contextSnapshot,
  };

  hooks.setTasks((prev) => [localTask, ...prev]);

  try {
    onDebug?.('正在创建视频生成任务...');
    const metadataItems = buildVideoGenerationMetadataItems(nodes);
    if (metadataItems.length) {
      onDebug?.(`将引用 ${metadataItems.length} 个已激活素材参与生成（content）`);
    }

    const requestBody: VideoGenerationPayload = {
      model,
      prompt,
      duration,
      ratio,
      resolution,
      metadataItems,
    };

    const requestForApi = buildVideoGenerationRequestBody(requestBody);
    onDebug?.(`POST ${VIDEO_GENERATION_BASE_URL}/v1/video/generations`);
    onDebug?.(`Body: ${jsonStringifyForDebug(requestForApi)}…`);

    const result = await createVideoGeneration(requestBody, {
      baseUrl: VIDEO_GENERATION_BASE_URL,
      apiKey,
    });

    const resultData = result?.data && typeof result.data === 'object' ? result.data : result;
    const successFlag =
      typeof result?.success === 'boolean'
        ? result.success
        : typeof resultData?.success === 'boolean'
          ? resultData.success
          : undefined;
    if (successFlag === false) {
      const apiMessage = extractApiMessage(resultData) || extractApiMessage(result) || '请求失败';
      throw new Error(String(apiMessage));
    }

    const taskId = String(
      resultData.id ||
        resultData.remote_task_id ||
        resultData.remoteTaskId ||
        result.id ||
        resultData.task_id ||
        result.task_id ||
        '',
    );
    if (!taskId) throw new Error('未获取到任务ID');

    onDebug?.(`任务创建成功: ${taskId}`);
    hooks.setTasks((prev) =>
      prev.map((t) =>
        t.id === localId
          ? {
              ...t,
              id: taskId,
              remoteTaskId: taskId,
              status: normalizeStatus(resultData.status || result.status || 'queued'),
            }
          : t,
      ),
    );
    hooks.ensurePolling(taskId);
    return { taskId, localTask: { ...localTask, id: taskId, remoteTaskId: taskId } };
  } catch (err: unknown) {
    const errMsg =
      extractApiMessage(err) ||
      (err instanceof Error ? err.message : '') ||
      String(err) ||
      '请求失败';
    hooks.setTasks((prev) =>
      prev.map((t) =>
        t.id === localId ? { ...t, status: 'failed' as TaskStatus, error: errMsg } : t,
      ),
    );
    throw new Error(errMsg);
  }
}
